/**
 * Shared story text processor - CANONICAL source of truth for both LoadingStory and PastStoryRevolutionary.
 * 
 * Both pages MUST use this to ensure IDENTICAL text chunks for TTS cache consistency.
 * If LoadingStory generates chunks with different text than PastStory reads,
 * IndexedDB cache keys won't match and audio playback will be slow (5-10s lag).
 * 
 * DO NOT duplicate or modify chunking/section logic in individual page files.
 */

export const MAX_CHUNK_CHARS = 600;

// Clean any leaked technical markers from narration text
// MUST match PastStoryRevolutionary exactly
export const cleanNarrationText = (text) => {
  if (!text) return '';
  return text
    .replace(/---\s*[A-Z_]+\s*---/gi, '')
    .replace(/===\s*[A-Z_]+\s*===/gi, '')
    .replace(/\b[A-Z_]{4,}---/g, '')
    .replace(/\bFUTURE_MD\b/g, '')
    .replace(/\bPAST_MD\b/g, '')
    .replace(/\bFUTURE_NARRATIVES\b/g, '')
    .replace(/\bPAST_NARRATIVES\b/g, '')
    .replace(/###SECT_[A-Z_]+###/g, '')
    .replace(/###\s*/g, '')
    .replace(/---+/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/#{1,3}\s+/g, '')
    .replace(/^\s*OVERVIEW\s*$/gim, '')
    .replace(/^\s*Domain\s*\d+\s*$/gim, '')
    .replace(/^\s*\w+\s+Mahadasha\s*\(\d{4}\s*[-\u2013to]+\s*\d{4}\)\s*\n?/i, '')
    .replace(/\n\s*\w+\s+Mahadasha\s*\(\d{4}\s*[-\u2013to]+\s*\d{4}\)\s*\n/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Split text into chunks at sentence boundaries
// CANONICAL version - used by both LoadingStory and PastStory
export const splitTextIntoChunks = (text, maxChars = MAX_CHUNK_CHARS) => {
  if (!text || text.length <= maxChars) return text ? [text] : [];

  const chunks = [];
  let remaining = text;

  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining.trim());
      break;
    }

    let breakPoint = maxChars;
    for (const endChar of ['. ', '\u0964 ', '? ', '! ', '\n\n', '\n', ', ']) {
      const lastIndex = remaining.substring(0, maxChars).lastIndexOf(endChar);
      if (lastIndex > maxChars * 0.5) {
        breakPoint = lastIndex + endChar.length;
        break;
      }
    }

    chunks.push(remaining.substring(0, breakPoint).trim());
    remaining = remaining.substring(breakPoint).trim();
  }

  return chunks;
};

// Detect which mahadasha lord a paragraph primarily discusses (scoring version)
const detectMahadashaFromText = (text, pastMds) => {
  if (!text || !pastMds || pastMds.length === 0) return { index: -1, lord: null };

  let bestMatch = { index: -1, lord: null, score: 0 };

  pastMds.forEach((md, idx) => {
    const lord = (md.lord || '').toLowerCase();
    if (!lord) return;

    let score = 0;
    const lordRegex = new RegExp(lord, 'gi');
    const mentions = (text.match(lordRegex) || []).length;
    score += mentions * 2;

    const explicitPatterns = [
      new RegExp(`${lord}\\s*(ki|ka|ke|'s)?\\s*(dasha|mahadasha|period|phase)`, 'i'),
      new RegExp(`(during|under|in)\\s+${lord}`, 'i'),
      new RegExp(`${lord}\\s+(ke dauran|ke samay|mein|influence)`, 'i'),
      new RegExp(`^${lord}\\s*:`, 'i'),
      new RegExp(`${lord}\\s+mahadasha\\s*\\(`, 'i')
    ];
    for (const p of explicitPatterns) {
      if (p.test(text)) score += 5;
    }

    const years = md.years || '';
    if (years && text.toLowerCase().includes(years.toLowerCase())) score += 3;

    if (score > bestMatch.score) {
      bestMatch = { index: idx, lord: md.lord, score };
    }
  });

  return bestMatch.score >= 4 ? bestMatch : { index: -1, lord: null };
};

// Generate intro text for a language (PATH 4 fallback)
const generateIntroText = (language, userName, mahadashaCount) => {
  if (language === 'hindi') {
    return `\u0928\u092e\u0938\u094d\u0924\u0947 ${userName}!\n\n\u092e\u0948\u0902 \u0906\u092a\u0915\u094b \u0906\u092a\u0915\u0947 \u092c\u0940\u0924\u0947 \u0939\u0941\u090f \u091c\u0940\u0935\u0928 \u0915\u0940 \u0915\u0939\u093e\u0928\u0940 \u0938\u0941\u0928\u093e\u0928\u0947 \u091c\u093e \u0930\u0939\u093e \u0939\u0942\u0902\u0964 \u0906\u092a\u0915\u0947 ${mahadashaCount} \u092e\u0939\u093e\u0926\u0936\u093e\u0913\u0902 \u0915\u093e \u0938\u092b\u0930\u0964`;
  } else if (language === 'english') {
    return `Hello ${userName}!\n\nLet me tell you the story of your past life. Your journey through ${mahadashaCount} Mahadashas.`;
  } else {
    return `Namaste ${userName}!\n\nMain aapko aapke beete huye jeevan ki kahani sunane ja raha hoon. Aapke ${mahadashaCount} Mahadashas ka safar.`;
  }
};

// Format engine prediction into readable text
const formatPrediction = (pred, domain, lordName) => {
  if (!pred || typeof pred !== 'object') return '';
  const parts = [];
  const hinglishSummary = pred.hinglish_summary;
  const detailedSummary = pred.detailed_summary || pred.summary || '';
  if (hinglishSummary) {
    parts.push(hinglishSummary);
  } else if (detailedSummary) {
    parts.push(detailedSummary);
  }
  const focus = pred.focus_areas;
  if (Array.isArray(focus) && focus.length > 0) {
    parts.push(`Focus areas: ${focus.join(', ')}.`);
  }
  const advice = pred.advice;
  if (advice && typeof advice === 'string') {
    parts.push(advice);
  }
  const risks = pred.disease_risks;
  if (Array.isArray(risks) && risks.length > 0) {
    parts.push(`Risks: ${risks.join(', ')}.`);
  }
  return parts.join(' ');
};

// Extract lord-specific sentences from a combined narrative
const extractLordSentences = (text, lordName) => {
  if (!text || !lordName) return text;
  const allLords = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu',
    'Surya', 'Chandra', 'Mangal', 'Budh', 'Guru', 'Shukra', 'Shani'];
  const otherLords = allLords.filter(l => l.toLowerCase() !== lordName.toLowerCase());
  const mentionsOtherLords = otherLords.some(l => text.toLowerCase().includes(l.toLowerCase() + ' '));
  if (!mentionsOtherLords) return null;

  const sentences = text.split(/(?<=[.!?\u0964])\s+/).filter(s => s.trim().length > 10);
  const lordLower = lordName.toLowerCase();
  const lordAliases = { 'Sun': 'surya', 'Moon': 'chandra', 'Mars': 'mangal', 'Mercury': 'budh',
    'Jupiter': 'guru', 'Venus': 'shukra', 'Saturn': 'shani', 'Rahu': 'rahu', 'Ketu': 'ketu' };
  const alias = lordAliases[lordName] || '';
  const lordSpecific = sentences.filter(s => {
    const sl = s.toLowerCase();
    return sl.includes(lordLower) || (alias && sl.includes(alias));
  });
  return lordSpecific.length > 0 ? lordSpecific.join(' ') : null;
};

/**
 * Build story sections from unified data — CANONICAL version.
 * Returns { fullText: string, sections: Array<{ text, mahadashaIndex, type, domain, mahadashaLord, isPart1 }> }
 * 
 * This is the EXACT logic from PastStoryRevolutionary's generateFullStoryText.
 * Both LoadingStory and PastStory MUST use this to produce identical chunks.
 * 
 * @param {Object} data - Full unified story data (NOT just data.past_story)
 * @param {string} language - 'hindi' | 'hinglish' | 'english'
 */
export const buildStoryData = (data, language) => {
  if (!data) return { fullText: '', sections: [] };

  const pastMds = data.past_story?.mahadashas ||
    data.all_mahadashas?.filter(m => m.status === 'past' || m.status === 'current') || [];
  const userName = data.user_info?.name || 'User';

  let fullText = '';
  const sections = [];

  const langText = {
    hindi: {
      intro: `${userName}, \u0906\u091c \u092e\u0948\u0902 \u0906\u092a\u0915\u094b \u0906\u092a\u0915\u0940 \u092a\u0942\u0930\u0940 \u092a\u093f\u091b\u0932\u0940 \u0915\u0939\u093e\u0928\u0940 \u0938\u0941\u0928\u093e\u0924\u093e \u0939\u0942\u0902\u0964 \u0906\u092a\u0915\u0940 \u091c\u0928\u094d\u092e \u0915\u0941\u0902\u0921\u0932\u0940 \u0915\u0947 \u090f\u0932\u094d\u0917\u094b\u0930\u093f\u0926\u092e \u0938\u0947 \u0928\u093f\u0915\u093e\u0932\u0940 \u0917\u0908 \u092f\u0939 \u0915\u0939\u093e\u0928\u0940 \u0939\u0948 - \u0939\u0930 \u0915\u094d\u0937\u0947\u0924\u094d\u0930, \u0939\u0930 \u092e\u0939\u093e\u0926\u0936\u093e, \u0938\u092c \u0915\u0941\u091b\u0964`,
    },
    english: {
      intro: `${userName}, today I will tell you your complete past story. This story is derived from your birth chart algorithm - every domain, every mahadasha, everything.`,
    },
    hinglish: {
      intro: `${userName}, aaj main aapko aapki poori past story sunata hoon. Aapki birth chart ke algorithm se nikali gayi ye kahani hai - har domain, har mahadasha, sab kuch.`,
    }
  };

  // eslint-disable-next-line no-unused-vars
  const lang = langText[language] || langText.hinglish;

  // ===== Check for pre-parsed sections from backend =====
  const storySections = data.past_story?.sections || {};
  const introStories = data.past_story?.intro || {};
  const algoDriven = data.past_story?.algorithm_driven || {};

  // PATH 1: Pre-parsed sections from backend (best quality)
  if (storySections[language] && Array.isArray(storySections[language]) && storySections[language].length > 0) {
    storySections[language].forEach((section) => {
      fullText += section.text + '\n\n';
      sections.push({
        text: section.text,
        mahadashaIndex: section.mahadasha_index ?? -1,
        type: section.type,
        domain: section.domain,
        mahadashaLord: section.mahadasha_lord,
        isPart1: section.type === 'intro' || section.type === 'domain'
      });
    });

  // PATH 2: Build sections from algorithm_driven structured data
  } else if (algoDriven && (algoDriven.intro?.[language] || algoDriven.domain_sections?.[language] || algoDriven.stories?.[language])) {
    // 2a. Add intro
    const algoIntro = algoDriven.intro?.[language];
    if (algoIntro) {
      fullText += algoIntro + '\n\n';
      sections.push({ text: algoIntro, mahadashaIndex: -1, type: 'intro', domain: null, mahadashaLord: null, isPart1: true });
    }

    // 2b+2c. DOMAIN-CENTRIC: For each domain -> overview -> all MDs for that domain
    const domainSections = algoDriven.domain_sections?.[language] || {};
    const domainOrder = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
    const mdSections = algoDriven.mahadasha_sections?.[language] || [];
    const pastMdSections = algoDriven.past_mahadasha_sections?.[language];
    const targetMdSections = pastMdSections || mdSections;

    // Build domain overview text map for duplicate detection
    const domainOverviewMap = {};
    for (const [dom, ds] of Object.entries(domainSections)) {
      const ov = typeof ds === 'object' ? (ds?.narrative || '') : (typeof ds === 'string' ? ds : '');
      if (ov) domainOverviewMap[dom] = ov.substring(0, 80);
    }

    // Build per-lord domain_predictions map from all_mahadashas (engine results)
    const allMdPredictions = {};
    const allMds = data.all_mahadashas || [];
    for (const amd of allMds) {
      const lord = amd.lord || amd.name;
      if (lord && amd.domain_predictions) {
        allMdPredictions[lord] = amd.domain_predictions;
      }
    }

    // Helper: resolve MD text for a specific domain
    const resolveMdDomainText = (md, idx, domain) => {
      const mdLord = (typeof md === 'object' ? md?.lord : null) || pastMds[idx]?.lord || 'Unknown';
      const domainImpacts = typeof md === 'object' ? md?.domain_impacts : null;
      if (!domainImpacts || typeof domainImpacts !== 'object' || Object.keys(domainImpacts).length === 0) return null;

      let impactText = domainImpacts[domain];
      let finalText = '';

      if (impactText && typeof impactText === 'string' && impactText.length > 20) {
        const isDuplicateOfOverview = domainOverviewMap[domain] &&
          impactText.substring(0, 80) === domainOverviewMap[domain];
        if (isDuplicateOfOverview) {
          const enginePred = allMdPredictions[mdLord]?.[domain] ||
            allMdPredictions[mdLord]?.[domain === 'spirituality' ? 'spiritual' : domain];
          finalText = formatPrediction(enginePred, domain, mdLord);
        } else {
          const extracted = extractLordSentences(impactText, mdLord);
          if (extracted) {
            finalText = extracted;
          } else {
            const enginePred = allMdPredictions[mdLord]?.[domain] ||
              allMdPredictions[mdLord]?.[domain === 'spirituality' ? 'spiritual' : domain];
            const engineText = formatPrediction(enginePred, domain, mdLord);
            finalText = engineText || impactText;
          }
        }
      }

      if (!finalText || finalText.length < 20) {
        const domKey = domain === 'spirituality' ? 'spiritual' : domain;
        const enginePred = allMdPredictions[mdLord]?.[domKey] || allMdPredictions[mdLord]?.[domain];
        finalText = formatPrediction(enginePred, domain, mdLord);
      }

      return (finalText && finalText.length > 15) ? { text: finalText, mdLord } : null;
    };

    // DOMAIN-CENTRIC LOOP: For each domain -> overview -> all MDs -> next domain
    const oldFormatMDs = [];

    for (const domain of domainOrder) {
      const ds = domainSections[domain];
      const overviewText = typeof ds === 'object' ? (ds?.narrative || '') : (typeof ds === 'string' ? ds : '');
      if (overviewText && overviewText.length > 30 && !overviewText.toLowerCase().includes('analysis pending')) {
        fullText += overviewText + '\n\n';
        sections.push({ text: overviewText, mahadashaIndex: -1, type: 'domain', domain, mahadashaLord: null, isPart1: true });
      }

      if (Array.isArray(targetMdSections)) {
        targetMdSections.forEach((md, idx) => {
          const mdLord = (typeof md === 'object' ? md?.lord : null) || pastMds[idx]?.lord || 'Unknown';
          const domainImpacts = typeof md === 'object' ? md?.domain_impacts : null;

          if (domainImpacts && typeof domainImpacts === 'object' && Object.keys(domainImpacts).length > 0) {
            const resolved = resolveMdDomainText(md, idx, domain);
            if (resolved) {
              fullText += resolved.text + '\n\n';
              sections.push({
                text: resolved.text, mahadashaIndex: idx, type: 'mahadasha_domain', domain,
                mahadashaLord: resolved.mdLord, isPart1: false
              });
            }
          } else if (domain === domainOrder[0]) {
            oldFormatMDs.push({ md, idx, mdLord });
          }
        });
      }
    }

    // Append old-format mahadashas (no domain_impacts) at the end
    for (const { md, idx, mdLord } of oldFormatMDs) {
      const narrative = typeof md === 'object' ? (md?.narrative || '') : (typeof md === 'string' ? md : '');
      if (narrative && narrative.length > 30) {
        fullText += narrative + '\n\n';
        sections.push({
          text: narrative, mahadashaIndex: idx, type: 'mahadasha', domain: null,
          mahadashaLord: mdLord, isPart1: false
        });
      }
    }

    // 2d. If still no domain/mahadasha sections, use the full stories text
    if (sections.length <= 1) {
      const fullStory = algoDriven.stories?.[language] || '';
      if (fullStory && fullStory.length > 200) {
        const paragraphs = cleanNarrationText(fullStory).split(/\n\n+/).filter(p => p.trim().length > 50);
        paragraphs.forEach((para, idx) => {
          fullText += para + '\n\n';
          const detected = detectMahadashaFromText(para, pastMds);
          const isIntroOrDomain = idx < Math.min(10, paragraphs.length / 3);
          sections.push({
            text: para.trim(),
            mahadashaIndex: detected.index !== -1 ? detected.index : (isIntroOrDomain ? -1 : Math.min(idx - 10, pastMds.length - 1)),
            mahadashaLord: detected.lord,
            type: detected.index !== -1 ? 'mahadasha' : (idx === 0 ? 'intro' : (isIntroOrDomain ? 'domain' : 'mahadasha')),
            isPart1: isIntroOrDomain && detected.index === -1
          });
        });
      }
    }

  // PATH 3: Full story text in intro field (long string > 500 chars)
  } else if (introStories[language] && typeof introStories[language] === 'string' && introStories[language].length > 500) {
    const godLevelStory = cleanNarrationText(introStories[language]);

    const paragraphs = godLevelStory.split(/\n\n+/).filter(p => p.trim().length > 50);

    paragraphs.forEach((para, idx) => {
      fullText += para + '\n\n';
      const detected = detectMahadashaFromText(para, pastMds);
      const isIntroOrDomain = idx < Math.min(10, paragraphs.length / 3) && detected.index === -1;
      sections.push({
        text: para.trim(),
        mahadashaIndex: detected.index !== -1 ? detected.index : (isIntroOrDomain ? -1 : Math.min(idx - 10, pastMds.length - 1)),
        mahadashaLord: detected.lord,
        type: detected.index !== -1 ? 'mahadasha' : (idx === 0 ? 'intro' : (isIntroOrDomain ? 'domain' : 'mahadasha')),
        isPart1: isIntroOrDomain
      });
    });

  } else {
    // PATH 4: Old structure fallback
    const intro = data.past_story?.intro?.[language] || generateIntroText(language, userName, pastMds.length);
    if (intro) {
      fullText += intro + '\n\n';
      sections.push({ text: intro, mahadashaIndex: -1, type: 'intro', isPart1: true });
    }

    const timelineNarrations = data.past_story?.timeline_narrations?.[language] || [];
    const effectiveMds = pastMds.length > 0 ? pastMds :
      (data.all_mahadashas || []).filter(m => m.status === 'past' || m.status === 'current');

    effectiveMds.forEach((md, idx) => {
      if (timelineNarrations[idx]) {
        fullText += timelineNarrations[idx] + '\n\n';
        sections.push({
          text: timelineNarrations[idx], mahadashaIndex: idx, type: 'narration', isPart1: false,
          mahadashaLord: md.lord, mahadashaYears: md.years, mahadashaColor: md.color
        });
      }
    });

    // PATH 4b: If still very short, also check domain_narratives
    const domainNarratives = data.past_story?.domain_narratives;
    if (domainNarratives && sections.length <= 1) {
      const domainOrder = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
      for (const domain of domainOrder) {
        const dn = domainNarratives[domain]?.[language] || domainNarratives[domain];
        const text = typeof dn === 'string' ? dn : (typeof dn === 'object' ? (dn?.narrative || dn?.text || '') : '');
        if (text && text.length > 30) {
          fullText += text + '\n\n';
          sections.push({ text, mahadashaIndex: -1, type: 'domain', domain, isPart1: true });
        }
      }
    }
  }

  // ===== SUPPLEMENTAL PASS: Create mahadasha_domain chunks if missing =====
  const hasMahadashaDomainSections = sections.some(s => s.type === 'mahadasha_domain');
  if (!hasMahadashaDomainSections && pastMds.length > 0) {
    const domainOverviewMap = {};
    sections.filter(s => s.type === 'domain').forEach(s => {
      if (s.domain && s.text) domainOverviewMap[s.domain] = s.text.substring(0, 80);
    });

    const allMdPredictions = {};
    const allMds = data.all_mahadashas || [];
    for (const amd of allMds) {
      const lord = amd.lord || amd.name;
      if (lord && amd.domain_predictions) allMdPredictions[lord] = amd.domain_predictions;
    }

    const algoMdSections = algoDriven.mahadasha_sections?.[language] ||
      algoDriven.past_mahadasha_sections?.[language] || [];

    const fmtPred = (pred) => {
      if (!pred || typeof pred !== 'object') return '';
      const parts = [];
      if (pred.hinglish_summary) parts.push(pred.hinglish_summary);
      else if (pred.detailed_summary || pred.summary) parts.push(pred.detailed_summary || pred.summary);
      if (Array.isArray(pred.focus_areas) && pred.focus_areas.length) parts.push(`Focus areas: ${pred.focus_areas.join(', ')}.`);
      if (pred.advice) parts.push(pred.advice);
      if (Array.isArray(pred.disease_risks) && pred.disease_risks.length) parts.push(`Risks: ${pred.disease_risks.join(', ')}.`);
      return parts.join(' ');
    };

    const extractForLord = (text, lordName) => {
      if (!text || !lordName) return null;
      const otherLords = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu',
        'Surya','Chandra','Mangal','Budh','Guru','Shukra','Shani'].filter(l => l.toLowerCase() !== lordName.toLowerCase());
      if (!otherLords.some(l => text.toLowerCase().includes(l.toLowerCase() + ' '))) return null;
      const sentences = text.split(/(?<=[.!?\u0964])\s+/).filter(s => s.trim().length > 10);
      const lordLower = lordName.toLowerCase();
      const aliases = {'Sun':'surya','Moon':'chandra','Mars':'mangal','Mercury':'budh',
        'Jupiter':'guru','Venus':'shukra','Saturn':'shani','Rahu':'rahu','Ketu':'ketu'};
      const alias = aliases[lordName] || '';
      const specific = sentences.filter(s => { const sl = s.toLowerCase(); return sl.includes(lordLower) || (alias && sl.includes(alias)); });
      return specific.length > 0 ? specific.join(' ') : null;
    };

    const impactDomains = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];

    pastMds.forEach((md, idx) => {
      const mdLord = md.lord || 'Unknown';
      const algoMd = algoMdSections[idx];
      const domainImpacts = algoMd?.domain_impacts || {};

      for (const domain of impactDomains) {
        let finalText = '';
        const diText = domainImpacts[domain];

        if (diText && typeof diText === 'string' && diText.length > 20) {
          const isDup = domainOverviewMap[domain] && diText.substring(0, 80) === domainOverviewMap[domain];
          if (!isDup) {
            const extracted = extractForLord(diText, mdLord);
            if (extracted) finalText = extracted;
          }
        }

        if (!finalText || finalText.length < 20) {
          const domKey = domain === 'spirituality' ? 'spiritual' : domain;
          const pred = allMdPredictions[mdLord]?.[domKey] || allMdPredictions[mdLord]?.[domain];
          finalText = fmtPred(pred);
        }

        if (finalText && finalText.length > 15) {
          fullText += finalText + '\n\n';
          sections.push({
            text: finalText, mahadashaIndex: idx, type: 'mahadasha_domain', domain,
            mahadashaLord: mdLord, isPart1: false
          });
        }
      }
    });
  }

  // ===== AUTO-DETECT DOMAIN FOR SECTIONS MISSING IT =====
  // PATH 3, PATH 4, PATH 2d fallbacks don't set domain property.
  // Without domain, filtering is broken. Detect domain from text keywords.
  const domainKeywords = {
    health: ['health', 'disease', 'medical', 'body', 'physical', 'illness', 'fitness', 'hospital',
      'swasthya', 'bimari', 'sharir', 'sehat', 'rog', 'chikitsa', 'doctor', 'treatment',
      '\u0938\u094d\u0935\u093e\u0938\u094d\u0925\u094d\u092f', '\u0930\u094b\u0917', '\u0936\u0930\u0940\u0930', '\u092c\u0940\u092e\u093e\u0930\u0940'],
    career: ['career', 'job', 'profession', 'work', 'business', 'employment', 'promotion', 'office',
      'kaam', 'naukri', 'vyapar', 'karya', 'pesha', 'udyog', 'karobar',
      '\u0915\u0930\u093f\u092f\u0930', '\u0928\u094c\u0915\u0930\u0940', '\u0935\u094d\u092f\u093e\u092a\u093e\u0930', '\u0915\u093e\u0930\u094d\u092f'],
    wealth: ['wealth', 'money', 'financial', 'income', 'property', 'investment', 'profit', 'loss',
      'dhan', 'paisa', 'arthik', 'sampatti', 'nivesh', 'labh', 'aay',
      '\u0927\u0928', '\u0905\u0930\u094d\u0925', '\u0938\u0902\u092a\u0924\u094d\u0924\u093f', '\u0906\u092f'],
    marriage: ['marriage', 'spouse', 'partner', 'relationship', 'love', 'wedding', 'wife', 'husband',
      'vivah', 'shaadi', 'pati', 'patni', 'rishta', 'pyar', 'jeevan saathi',
      '\u0935\u093f\u0935\u093e\u0939', '\u0936\u093e\u0926\u0940', '\u092a\u0924\u093f', '\u092a\u0924\u094d\u0928\u0940'],
    education: ['education', 'study', 'learning', 'knowledge', 'school', 'college', 'degree', 'exam',
      'shiksha', 'padhai', 'vidya', 'gyan', 'pariksha',
      '\u0936\u093f\u0915\u094d\u0937\u093e', '\u092a\u0922\u093c\u093e\u0908', '\u0935\u093f\u0926\u094d\u092f\u093e'],
    family: ['family', 'parents', 'children', 'mother', 'father', 'siblings', 'home',
      'parivar', 'mata', 'pita', 'bacche', 'ghar', 'santan',
      '\u092a\u0930\u093f\u0935\u093e\u0930', '\u092e\u093e\u0924\u093e', '\u092a\u093f\u0924\u093e', '\u0938\u0902\u0924\u093e\u0928'],
    travel: ['travel', 'foreign', 'abroad', 'journey', 'relocation', 'move', 'migration',
      'yatra', 'videsh', 'safar', 'pravas',
      '\u092f\u093e\u0924\u094d\u0930\u093e', '\u0935\u093f\u0926\u0947\u0936', '\u092a\u094d\u0930\u0935\u093e\u0938'],
    spirituality: ['spiritual', 'religion', 'meditation', 'dharma', 'karma', 'moksha', 'prayer', 'temple',
      'adhyatmik', 'dharm', 'pooja', 'mandir', 'sadhna', 'bhakti',
      '\u0906\u0927\u094d\u092f\u093e\u0924\u094d\u092e\u093f\u0915', '\u0927\u0930\u094d\u092e', '\u092d\u0915\u094d\u0924\u093f', '\u092e\u094b\u0915\u094d\u0937']
  };

  const detectDomainFromText = (text) => {
    if (!text) return null;
    const lower = text.toLowerCase();
    let bestDomain = null;
    let bestScore = 0;
    for (const [domain, keywords] of Object.entries(domainKeywords)) {
      let score = 0;
      for (const kw of keywords) {
        const matches = lower.split(kw.toLowerCase()).length - 1;
        score += matches;
      }
      if (score > bestScore) {
        bestScore = score;
        bestDomain = domain;
      }
    }
    return bestScore >= 2 ? bestDomain : null;
  };

  // Apply domain detection to sections without domain property
  for (const section of sections) {
    if (!section.domain && section.type !== 'intro') {
      const detected = detectDomainFromText(section.text);
      if (detected) {
        section.domain = detected;
        // Also upgrade type for better filtering
        if (section.type === 'mahadasha' && section.mahadashaIndex >= 0) {
          section.type = 'mahadasha_domain';
        } else if (section.type !== 'mahadasha' && section.type !== 'mahadasha_domain') {
          section.type = 'domain';
        }
      }
    }
  }

  // ===== REORDER SECTIONS: Domain-centric sequence =====
  const domainOrder2 = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
  const introSections = sections.filter(s => s.type === 'intro');
  const reordered = [...introSections];

  for (const dom of domainOrder2) {
    const overviews = sections.filter(s => s.type === 'domain' && (s.domain === dom || s.domain === dom.replace('spirituality', 'spiritual')));
    reordered.push(...overviews);
    const mds = sections
      .filter(s => s.type === 'mahadasha_domain' && (s.domain === dom || s.domain === dom.replace('spirituality', 'spiritual')))
      .sort((a, b) => (a.mahadashaIndex || 0) - (b.mahadashaIndex || 0));
    reordered.push(...mds);
  }

  const remaining = sections.filter(s => !reordered.includes(s) && s.type !== 'intro');
  reordered.push(...remaining);

  const reorderedText = reordered.map(s => s.text).join('\n\n');

  return { fullText: reorderedText.trim(), sections: reordered };
};

/**
 * Generate TTS chunk texts from unified story data.
 * Returns flat array of text strings - each ready to be sent to Edge TTS.
 * These EXACT texts will be used by PastStory for cache key generation.
 * 
 * @param {Object} data - Full unified story data (NOT just data.past_story)
 * @param {string} language - 'hindi' | 'hinglish' | 'english'
 */
export const generateTTSChunkTexts = (data, language) => {
  const { sections } = buildStoryData(data, language);
  const chunkTexts = [];

  sections.forEach(section => {
    const sectionChunks = splitTextIntoChunks(section.text, MAX_CHUNK_CHARS);
    chunkTexts.push(...sectionChunks);
  });

  return chunkTexts;
};


/**
 * Get the EXACT section texts that PastStoryRevolutionary uses for TTS.
 * Each text is the full section text (up to 500 chars), NOT split into smaller chunks.
 * Use this in LoadingStory to pre-generate audio that PastStory can read from globalAudioStore.
 * 
 * @param {Object} data - Full unified story data
 * @param {string} language - 'hindi' | 'hinglish' | 'english'
 * @returns {string[]} Array of section texts matching PastStory's audioChunks
 */
export const getPastStorySectionTexts = (data, language) => {
  const { sections } = buildStoryData(data, language);
  return sections
    .map(s => s.text)
    .filter(t => t && t.length >= 5);
};
